/**
 * Remind, Deduct and clear on each Action Center row (Traci's doc, Oct 2026: "Please add two buttons to each row: The
 * first is 'Remind' which when clicked by talent coord, sends an auto generated message to the talent to reach out
 * (portal chat and SMS text). The second button is 'Deduct' which when clicked, logs an auto note in the booking notes
 * with talent name and -25 for late reachout. Add the clear 'x' logic to each row with pop-up 'are you sure' message to
 * confirm clearing."). Pure: the row in, the words and amounts out.
 *
 * Remind is on the rows where the talent has something to do. Urgent Coverage has no talent yet, and the Box folder is
 * the coordinator's own job, so those rows only clear. Deduct follows the pay deduction schedule (deductions.js):
 * Late Reachout $25 on Client Contact; Late Upload by days late on OVERDUE Media/Form. The note is for Billing, who
 * assesses the deduction itself (Assess Deduction stays Admin and Billing).
 */
import { MEDIA_DUE_DAYS } from './actionCenter.js'

const REMIND_KINDS = new Set(['client_contact', 'five_day_checkin', 'day_of_checkin', 'insurance_form', 'media_overdue'])
export const canRemind = kind => REMIND_KINDS.has(kind)
export const canDeduct = kind => kind === 'client_contact' || kind === 'media_overdue'

const first = name => String(name || '').trim().split(/\s+/)[0] || 'there'
const dateWords = iso => {
  const d = new Date(`${String(iso || '').slice(0, 10)}T12:00:00Z`)
  return Number.isNaN(d.getTime()) ? 'the event' : d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

/**
 * The Business Controls template for a reminder (Portal chat, 2 Nov 2026) and its fields, filled the way reminderText
 * below fills them; reminderText stays as the reference wording. Null for a card with no reminder.
 */
export function reminderChat(kind, { talentName, clientName, eventDate, daysOut } = {}) {
  if (!canRemind(kind)) return null
  return {
    key: `chat_reminder_${kind}`,
    values: { first_name: first(talentName), client_name: clientName || 'your client', event_date: dateWords(eventDate), days_away: daysOut === 1 ? 'tomorrow' : `in ${daysOut} days`, due_days: String(MEDIA_DUE_DAYS) }
  }
}

/**
 * The reminder the talent gets, in the portal chat and by text.
 * @param kind  the Action Center card
 * @param ctx   { talentName, clientName, eventDate, daysOut }
 */
export function reminderText(kind, { talentName, clientName, eventDate, daysOut } = {}) {
  const hi = `Hi ${first(talentName)}, this is Love & Photos.`
  const client = clientName || 'your client'
  const when = dateWords(eventDate)
  switch (kind) {
    case 'client_contact': return `${hi} Friendly reminder to reach out to ${client} through your portal chat about their ${when} event. Client contact is due 3 weeks before the event.`
    case 'five_day_checkin': return `${hi} ${client}'s event is ${daysOut === 1 ? 'tomorrow' : `in ${daysOut} days`} (${when}). Please reply in your portal to confirm you're all set.`
    case 'day_of_checkin': return `${hi} Today is ${client}'s event. Please reply in your portal to confirm you're on your way.`
    case 'insurance_form': return `${hi} Please complete your insurance form for ${client}'s ${when} event in your portal. It's needed before the event.`
    case 'media_overdue': return `${hi} Your media and job completion form for ${client}'s ${when} event were due ${MEDIA_DUE_DAYS} days after the event. Please upload and submit them today.`
    default: return null
  }
}

/** Late Upload by days late (deductions.js schedule): 1 day $25 · 2 $50 · 3 $75 · 4 $100 · 5+ $200. */
export function lateUploadCents(daysLate) {
  const d = Math.max(0, Math.floor(Number(daysLate) || 0))
  if (d <= 0) return 0
  if (d >= 5) return 20000
  return d * 2500
}

/**
 * The deduction a Deduct click logs, or null for a card with none.
 * @param item  the Action Center row: kind, days_out (negative after the event)
 */
export function deductionFor(item) {
  if (item?.kind === 'client_contact') return { reason_key: 'late_reachout', reason_label: 'Late Reachout', amount_cents: 2500 }
  if (item?.kind === 'media_overdue') {
    const daysLate = -Number(item.days_out) - MEDIA_DUE_DAYS
    const cents = lateUploadCents(daysLate)
    return cents ? { reason_key: 'late_upload', reason_label: `Late Upload (${daysLate} day${daysLate === 1 ? '' : 's'} late)`, amount_cents: cents } : null
  }
  return null
}

/** The booking note a Deduct click writes. */
export function deductionNote({ talentName, roleLabel, deduction, by }) {
  return `Talent Pay Deduction logged by ${by || 'Talent'}: ${talentName || 'Talent'} (${roleLabel}) · −$${(deduction.amount_cents / 100).toFixed(2)} · ${deduction.reason_label}. For Billing to assess.`
}

/** The clear record's key, the same one the dashboard's Talent cards use, so a clear hides the row in both places. */
export const rowItemKey = item => `${item.booking_id}:${item.role || ''}`
